import "./HomeScreen.css";
import heroBackgroundMp4 from "../assets/heroBackground.mp4";
import heroBackgroundPoster from "../assets/poster.jpg";
import profileImage from "../assets/Profiles.webp";
import liveImage from "../assets/Live.webp";
import IRLImage from "../assets/IRL.webp";
import profileImageMobile from "../assets/ProfilesMobile.webp";
import liveImageMobile from "../assets/LiveMobile.webp";
import IRLImageMobile from "../assets/IRLMobile.webp";
import IG from "../assets/IG.png";

import { useRef, useState, useEffect } from "react";
import FooterItem from "../FooterItem";

const FEATURES = [
  {
    img: profileImage,
    imgMobile: profileImageMobile,
    alt: "App demo profile screen; not real user",
    title: "Video-first Profiles",
    desc: "Your first impression, unfiltered and alive.",
  },
  {
    img: liveImage,
    imgMobile: liveImageMobile,
    alt: "Live speed dating feature",
    title: "Live Speed Dating",
    desc: "Less swiping, more speaking.",
  },
  {
    img: IRLImage,
    imgMobile: IRLImageMobile,
    alt: "IRL events feature",
    title: "IRL Events & Meetups",
    desc: "Matches are better in person — meetups on the way.",
  },
];

const TOTAL_SECTIONS = 5;

function HomeScreen() {
  const [currentSection, setCurrentSection] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const isTransitioning = useRef(false);
  const touchStartY = useRef(null);
  const videoRef = useRef(null);
  const footerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function handleTouchMove(e) {
      const delta = touchStartY.current - e.touches[0].clientY;
      const swipingDown = delta < 0;
      if (currentSection === 0 && swipingDown) return;
      e.preventDefault();
    }

    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", handleTouchMove);
  }, [currentSection]);

  function toggleVideo() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }

  function goToSection(index) {
    if (isTransitioning.current) return;
    if (index < 0) return;
    if (index >= TOTAL_SECTIONS) index = 0;
    isTransitioning.current = true;
    setCurrentSection(index);
    setTimeout(() => {
      isTransitioning.current = false;
    }, 700);
  }

  function handleWheel(e) {
    if (e.deltaY > 20) goToSection(currentSection + 1);
    else if (e.deltaY < -20) goToSection(currentSection - 1);
  }

  function handleTouchStart(e) {
    touchStartY.current = e.touches[0].clientY;
  }

  function handleTouchEnd(e) {
    const delta = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(delta) < 30) return;
    goToSection(delta > 0 ? currentSection + 1 : currentSection - 1);
  }

  return (
    <div
      ref={containerRef}
      className="container"
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <title>Firstpeel Home Screen</title>

      <img src="/Logo.png" alt="logo" className="logo" />

      <div className="sectionDots">
        {Array.from({ length: TOTAL_SECTIONS }, (_, i) => (
          <div
            key={i}
            className={`sectionDot${i === currentSection ? " active" : ""}`}
            onClick={() => goToSection(i)}
          />
        ))}
      </div>

      {/* Section 0: Hero */}
      <div className={`section${currentSection === 0 ? " active" : ""}`}>
        <div className="heroVideoWrapper">
          <video
            ref={videoRef}
            className="mobileHeroBg"
            src={heroBackgroundMp4}
            poster={heroBackgroundPoster}
            autoPlay
            loop
            muted
            playsInline
            controls={false}
            preload="metadata"
            aria-hidden="true"
          />
          <button
            className="videoToggleBtn"
            onClick={toggleVideo}
            aria-label={isPlaying ? "Pause video" : "Play video"}
          >
            {isPlaying ? (
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="16"
                height="16"
              >
                <rect x="5" y="3" width="4" height="18" rx="1" />
                <rect x="15" y="3" width="4" height="18" rx="1" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="16"
                height="16"
              >
                <polygon points="5,3 19,12 5,21" />
              </svg>
            )}
          </button>
          <div className="mobileHeroContent">
            <h1 className="mobileHeroHeading">
              Photos don't show the real you.
            </h1>
            <h3 className="mobileHeroDescription">
              Try the dating app that does.
            </h3>
            <a
              className="mobileHeroDownloadButton"
              href="https://get.firstpeel.com"
            >
              <p className="mobileHeroDownloadText">Download Now</p>
            </a>
          </div>
        </div>
      </div>

      {/* Sections 1–3: Features */}
      {FEATURES.map((feature, i) => (
        <div
          key={i}
          className={`section featureSection${currentSection === i + 1 ? " active" : ""}`}
        >
          <div className="featureImageWrapper">
            <picture>
              <source srcSet={feature.imgMobile} media="(max-width: 599px)" />
              <img
                src={feature.img}
                alt={feature.alt}
                className="featureSectionImage"
              />
            </picture>
          </div>
          <div className="featureSectionText">
            <h2 className="featureSectionTitle">{feature.title}</h2>
            <p className="featureSectionDescription">{feature.desc}</p>
          </div>
        </div>
      ))}

      {/* Section 4: Footer */}
      <div
        className={`section footerSection${currentSection === 4 ? " active" : ""}`}
        ref={footerRef}
        onTouchEnd={(e) => {
          const delta = touchStartY.current - e.changedTouches[0].clientY;
          const el = footerRef.current;
          const atTop = !el || el.scrollTop <= 0;
          const atBottom =
            !el || el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
          if ((delta < -30 && atTop) || (delta > 30 && atBottom)) return;
          e.stopPropagation();
        }}
      >
        <div className="footerMessageWrapper">
          <h2 className="footerMessageHeader">And no more fake profiles.</h2>
          <p className="footerMessageDescription">
            Everyone on Firstpeel is face + age verified.
          </p>
        </div>

        <p className="footerMessage2">
          Built by two gen Z founders in SoCal who think people deserve better
          dating apps.
        </p>
        <div className="footerContainer">
          <div className="footerLogos">
            <img src="/Logo2.jpg" alt="logo2" className="logo2" />
            <a href="https://www.instagram.com/firstpeel/">
              <img src={IG} alt="Instagram" className="instagram" />
            </a>
          </div>
          <p className="messageText">
            18+ only. Must reside in the US. App previews use simulated profiles
            and stock video for demonstration. No actual users are shown.
          </p>
          <FooterItem />
        </div>
      </div>
    </div>
  );
}

export default HomeScreen;
